import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosGateway } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

/**
 * Create a new local patient (doctor-managed patient)
 */
export const createLocalPatient = createAsyncThunk(
  "localPatients/create",
  async (
    {
      firstName,
      lastName,
      dateOfBirth,
      age,
      email,
      phone,
      gender,
      notes,
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        firstName: String(firstName).trim(),
        lastName: String(lastName).trim(),
        dateOfBirth: String(dateOfBirth).trim(),
        ...(age ? { age: parseInt(age) } : {}),
        ...(email ? { email: String(email).trim() } : {}),
        ...(phone ? { phone: String(phone).trim() } : {}),
        ...(gender ? { gender: String(gender).trim() } : {}),
        ...(notes ? { notes: String(notes).trim() } : {}),
      };

      const { data } = await axiosGateway.post(
        `/local-patients${ENDPOINTS.LOCAL_PATIENT.CREATE()}`,
        payload
      );
      // API may return a wrapped payload: { statusCode, message, data: { ... } }
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to create local patient" }
      );
    }
  }
);

/**
 * Fetch all local patients for doctor
 */
export const fetchLocalPatients = createAsyncThunk(
  "localPatients/fetchAll",
  async ({ activeOnly } = {}, { rejectWithValue }) => {
    try {
      const url = `/local-patients${ENDPOINTS.LOCAL_PATIENT.ALL(activeOnly)}`;
      const opts = {
        // add a cache-busting param to bypass conditional 304 responses during debugging
        params: { _ts: Date.now() },
      };

      if (process.env.NODE_ENV !== "production") {
        try {
          console.debug("[fetchLocalPatients] requesting", { url, opts });
        } catch (e) {}
      }

      const response = await axiosGateway.get(url, opts);
      const data = response.data;

      if (process.env.NODE_ENV !== "production") {
        try {
          console.debug("[fetchLocalPatients] response", {
            status: response.status,
            headers: response.headers,
            bodySample: Array.isArray(data) ? data.slice(0, 3) : data,
          });
        } catch (e) {}
      }

      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to fetch local patients" }
      );
    }
  }
);

/**
 * Get single local patient by ID
 */
export const fetchLocalPatientById = createAsyncThunk(
  "localPatients/fetchById",
  async ({ patientId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosGateway.get(
        `/local-patients${ENDPOINTS.LOCAL_PATIENT.BY_ID(patientId)}`
      );
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to fetch local patient" }
      );
    }
  }
);

export const updateLocalPatient = createAsyncThunk(
  "localPatients/update",
  async ({ patientId, ...changes }, { rejectWithValue }) => {
    try {
      const { data } = await axiosGateway.put(
        `/local-patients${ENDPOINTS.LOCAL_PATIENT.UPDATE(patientId)}`,
        changes,
      );
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to update local patient" },
      );
    }
  },
);

const updateLocalPatientStatus = (type, path) =>
  createAsyncThunk(type, async ({ patientId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosGateway.put(
        `/local-patients${path(patientId)}`,
        {},
      );
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to update local patient" },
      );
    }
  });

export const deactivateLocalPatient = updateLocalPatientStatus(
  "localPatients/deactivate",
  ENDPOINTS.LOCAL_PATIENT.DEACTIVATE,
);

export const reactivateLocalPatient = updateLocalPatientStatus(
  "localPatients/reactivate",
  ENDPOINTS.LOCAL_PATIENT.REACTIVATE,
);

export const deleteLocalPatient = createAsyncThunk(
  "localPatients/delete",
  async ({ patientId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosGateway.delete(
        `/local-patients${ENDPOINTS.LOCAL_PATIENT.DELETE(patientId)}`,
      );
      return { patientId, data: data?.data ?? data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: "Failed to delete local patient" },
      );
    }
  },
);

/**
 * Create appointment + room in one call
 * Flow: Create rendezvous, then create room
 */
export const createAppointmentWithRoom = createAsyncThunk(
  "localPatients/createAppointmentWithRoom",
  async (
    {
      patientId,
      doctorId,
      appointmentData, // { startTime, endTime, reason, etc }
    },
    { rejectWithValue }
  ) => {
    try {
      // 1) Create the rendezvous using the ms-rendez-vous contract
      // Route: POST /rendezvous/medecin/:medecinId/patient/:patientId
      const rdvPayload = appointmentData;

      const rdvResponse = await axiosGateway.post(
        `/rendezvous/medecin/${encodeURIComponent(doctorId)}/patient/${encodeURIComponent(patientId)}`,
        rdvPayload
      );

      const appointment = rdvResponse.data;
      const appointmentId = appointment?.id || appointment?.appointmentId;

      if (!appointmentId) {
        throw new Error(
          "Appointment creation failed: no ID returned from server"
        );
      }

      // 2) A booking entered by the doctor is immediately confirmed. The
      // confirmation endpoint creates today's room when one does not exist.
      const confirmedResponse = await axiosGateway.patch(
        `/rendezvous/users/${encodeURIComponent(doctorId)}/${encodeURIComponent(appointmentId)}/confirm`,
        {}
      );
      const confirmedAppointment = confirmedResponse.data;

      let room = null;
      try {
        const roomResponse = await axiosGateway.get(
          `/rendezvous/appointment/${encodeURIComponent(appointmentId)}/room`
        );
        room = roomResponse.data;
      } catch (roomError) {
        // Confirmation is successful even when a room cannot be fetched.
        // The confirmed appointment remains visible in the Waiting Room.
        console.warn("Failed to retrieve the confirmed appointment room", roomError);
      }

      return {
        appointment: confirmedAppointment,
        room,
        success: true,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || {
          message: "Failed to create appointment and room",
        }
      );
    }
  }
);
